import React, { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";

import Avatar from "../avatar/Avatar";
import IdentityBadge from "./IdentityBadge";
import NDASignedBadge from "./NDASignedBadge";
import Modal from "../modal/Modal";
import Input from "../form-item/Input";
import LanguageSwitcher from "../tab/LanguageSwitcher";
import StatusIndicator from "../status-indicator/StatusIndicator";
import useMediaQuery from "../../hooks/useMediaQuery";
import SvgLoadingAnimation from "../loading/index";
import ToolTip from "../tooltip/ToolTip";
import ImageEditorModal from "../image-editor/ImageEditorModal";
import { CropResult } from "../image-editor/cropUtils";
import { ASPECT_RULES } from "../image-editor/aspectRule";
import AiImageGeneratorModal from "../ai-image-generator/AiImageGeneratorModal";
import type { GeneratedImageCandidate } from "../../app/services/aiImage";

// @ts-expect-error - JSファイルの型定義なし
import { userApi } from "../../app/accounts/authuser";
import {
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
} from "../../app/accounts/authuser";
import { useInitiateUploadMutation } from "../../app/services/uploadApi";
import type { SingleUploadData } from "../../app/services/uploadApi";
import {
  CardWrapper,
  OfferUserCardFrame,
  CardLabel,
  RotatedDisplayNameEn,
  ARC_AVATAR_SIZE_PX,
} from "../offer-wizard/OfferUserCard";

// Types
interface UserItemProps {
  size: 16 | 32 | 48 | 96 | 144;
  editable?: boolean;
  hideBadges?: boolean;
  /**
   * 表示バリアント。
   * - `"default"`（既定）: 中央寄せのプロフィール表示。
   * - `"card"`: オファー作成時のユーザーカードと同じデザインで表示し、アバター /
   *   テキストをクリックすると編集モーダルを開く（オンボーディングのアカウント設定用）。
   */
  variant?: "default" | "card";
  /** アバター編集状態が変わった時のコールバック（true=編集中, false=終了） */
  onAvatarEditingChange?: (isEditing: boolean) => void;
  /**
   * 表示を期待するユーザーの ID。指定時、/api/me のレスポンスに含まれる id と
   * 一致しない場合はエラーを表示して描画を抑止する（別ユーザーのプロフィールが
   * 誤って UI に表示される／誤って更新されるのを防ぐためのセーフティネット）。
   */
  expectedUserId?: number;
  /**
   * 表示を期待するユーザーのメールアドレス。指定時、/api/me のレスポンスに含まれる
   * email と一致しない場合はエラーを表示する。`expectedUserId` が取れない環境でも
   * 動作する軽量なガードとして使う。
   */
  expectedEmail?: string;
  /**
   * card バリアントのカード下部に表示する QR コードの値（公開プロフィール URL 等）。
   * 未指定なら QR は表示しない。
   */
  qrUrl?: string;
  /** QR コードのクリックハンドラ（例: マイサイト編集ページへの遷移）。 */
  onQrClick?: () => void;
}

// ===== Styled Components =====

const ProfileSection = styled.div.attrs({
  className: "u-w100 u-col-center u-ptb8",
})`
  position: relative;
  width: calc(100% - 32px);
  border-radius: 6px;
  transition: 0.2s;

  &:hover {
    background-color: var(--soremo-bg-blue);
    outline: 1px solid var(--soremo-blue);
    cursor: pointer;
  }
`;

// card バリアント: オファーカード内のテキスト領域（表示名 / タイトル / 所属）を
// クリック可能にするラッパー。クリックでプロフィール編集モーダルを日本語状態で開く
// （英語状態はカード左辺の RotatedDisplayNameEnTrigger から）。
const CardTextTrigger = styled.div<{ $editable: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  transition: background-color 0.2s;
  ${({ $editable }) => ($editable ? "cursor: pointer;" : "")}

  @media (hover: hover) {
    ${({ $editable }) =>
      $editable ? "&:hover { background-color: var(--soremo-bg-blue); }" : ""}
  }
`;

const ModalFooter = styled.div.attrs({
  className: "u-row u-justify-between u-w100 u-pt16",
})`
  position: relative;
  margin-top: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MismatchErrorPanel = styled.div.attrs({
  className: "bodytext-13",
})`
  color: var(--soremo-green);
  padding: 12px 16px;
  border: 1px solid var(--soremo-green);
  border-radius: 6px;
  text-align: center;
`;

// card バリアント下部の QR コード（OfferUserCardBody の QR と同じ視覚言語）。
// クリックで onQrClick（マイサイト編集ページ遷移等）を呼ぶ。
const QrTrigger = styled.button`
  display: inline-flex;
  margin-top: 8px;
  padding: 4px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid var(--soremo-border);
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  @media (hover: hover) {
    &:hover {
      border-color: var(--soremo-blue);
      box-shadow: 0 0 6px 0 var(--soremo-blue);
    }
  }
`;

// カード左辺の英語表示名。編集可能時はクリックでプロフィール編集モーダルを
// 英語状態で開く（共有定義は pointer-events: none のため編集時のみ解除）。
const RotatedDisplayNameEnTrigger = styled(RotatedDisplayNameEn)<{
  $editable: boolean;
}>`
  ${({ $editable }) =>
    $editable
      ? `
    pointer-events: auto;
    cursor: pointer;
    transition: opacity 0.2s;

    @media (hover: hover) {
      &:hover {
        opacity: 0.7;
      }
    }
  `
      : ""}
`;

// アバター未設定時のみ表示する「AIで生成」導線のアンカー。
// 通常フローに高さを追加しないオーバーレイ配置にすることで、
// アバターと表示名の間隔（既存レイアウト）を維持する。
const AvatarOverlayAnchor = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const AiGenerateOverlay = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 1;
`;

// 「AIで生成」導線。控えめなテキストリンク風の小ボタン。
const AiGenerateTrigger = styled.button.attrs({
  className: "bodytext-11",
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 16px;
  line-height: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 8px;
  color: var(--soremo-light-gray);
  transition: color 0.2s ease;

  .material-symbols-rounded {
    font-size: 14px;
  }

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
    }
  }
`;

/** 「AIで生成」ボタン（アイコン + ラベル）。配置は呼び出し側が決める。 */
const AiGenerateButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <AiGenerateTrigger type="button" onClick={onClick}>
    <span className="material-symbols-rounded">auto_awesome</span>
    AIで生成
  </AiGenerateTrigger>
);

// ===== Helpers =====

/**
 * プロフィール編集モーダルのフィールド定義（JP / EN 共通）。
 * `name` は `<key>_jp` / `<key>_en` として formData のキーになる。
 */
const PROFILE_FIELDS = [
  { key: "display_name", labelJp: "表示名", labelEn: "Display Name", option: "required" },
  { key: "title", labelJp: "タイトル", labelEn: "Title", option: "optional" },
  { key: "affiliation", labelJp: "所属", labelEn: "Affiliation", option: "optional" },
] as const;

/**
 * プロフィール編集モーダルを JP / EN の 2 カラム同時表示に切り替える最小幅。
 * `size="default"`（80vw・最大 756px）のモーダル内に 2 カラムが収まる幅。
 */
const PROFILE_TWO_COLUMN_QUERY = "(min-width: 900px)";

/**
 * `/api/me` の応答が、呼び出し側が期待するユーザーと一致するかを判定する。
 * いずれかの期待値が未指定なら、その観点ではチェックしない。
 */
const isExpectedUserMismatch = (
  userData: { id?: unknown; email?: unknown },
  expectedUserId: number | undefined,
  expectedEmail: string | undefined,
): boolean => {
  const userIdMismatch =
    typeof expectedUserId === "number" &&
    typeof userData.id === "number" &&
    userData.id !== expectedUserId;

  const userEmailMismatch =
    typeof expectedEmail === "string" &&
    expectedEmail.length > 0 &&
    typeof userData.email === "string" &&
    userData.email.toLowerCase() !== expectedEmail.toLowerCase();

  return userIdMismatch || userEmailMismatch;
};

// ===== S3 シングルアップロード =====

async function uploadFileToS3(
  file: File,
  presignedPost: SingleUploadData["presigned_post"],
): Promise<void> {
  const formData = new FormData();
  Object.entries(presignedPost.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  const response = await fetch(presignedPost.url, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`);
  }
}

// ===== Component =====

const UserItem: React.FC<UserItemProps> = ({
  size,
  editable = false,
  hideBadges = false,
  variant = "default",
  onAvatarEditingChange,
  expectedUserId,
  expectedEmail,
  qrUrl,
  onQrClick,
}) => {
  const dispatch = useDispatch();
  const { data: currentUser, isLoading, error } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [initiateUpload] = useInitiateUploadMutation();

  // プロフィール編集
  const [language, setLanguage] = useState<"JP" | "EN">("JP");
  // PC 等の広い画面では JP / EN を切り替えずに左右 2 カラムで同時表示する
  const isWideProfileForm = useMediaQuery(PROFILE_TWO_COLUMN_QUERY);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [updateStatus, setUpdateStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isComposingRef = useRef(false);

  // アバター編集
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [originalImageSrc, setOriginalImageSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalFileRef = useRef<File | null>(null);

  const userData = currentUser?.data || {};

  // ===== プロフィール編集ロジック =====

  const debouncedUpdate = useCallback(
    async (fieldName: string, fieldValue: string) => {
      try {
        setUpdateStatus("saving");
        await updateCurrentUser({ [fieldName]: fieldValue }).unwrap();
        setUpdateStatus("saved");
        setTimeout(() => setUpdateStatus("idle"), 1000);
      } catch (err: any) {
        console.error("Failed to update profile:", err);
        setUpdateStatus("error");
        setTimeout(() => setUpdateStatus("idle"), 3000);
      }
    },
    [updateCurrentUser],
  );

  const handleChange = (
    eOrName: React.ChangeEvent<HTMLInputElement> | string,
    valueArg?: string,
  ) => {
    let name: string, value: string;
    if (typeof eOrName === "string") {
      name = eOrName;
      value = valueArg!;
    } else {
      name = eOrName.target.name;
      value = eOrName.target.value;
    }

    if (isComposingRef.current) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      debouncedUpdate(name, value);
    }, 500);
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (
    e: React.CompositionEvent<HTMLInputElement>,
  ) => {
    isComposingRef.current = false;
    handleChange(e.currentTarget.name, e.currentTarget.value);
  };

  /** プロフィール編集モーダルの入力欄 1 つ分（言語別）。 */
  const renderProfileInput = (
    field: (typeof PROFILE_FIELDS)[number],
    lang: "JP" | "EN",
  ) => {
    const label = lang === "JP" ? field.labelJp : field.labelEn;
    const name = `${field.key}_${lang.toLowerCase()}`;
    return (
      <Input
        label={label}
        option={field.option}
        type="text"
        placeholder={label}
        width="100%"
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
      />
    );
  };

  /**
   * プロフィール編集モーダルを開く。クリック元（日本語部分 / 英語部分）に
   * 応じた言語状態で開けるよう、初期言語を引数で受け取る。
   * モーダル表示中はトリガーがオーバーレイに覆われるため、常に「閉→開」で呼ばれる。
   */
  const openProfileModal = (lang: "JP" | "EN") => {
    setFormData({
      display_name_jp: userData.display_name_jp || userData.stage_name || "",
      display_name_en: userData.display_name_en || userData.stage_name_en || "",
      title_jp: userData.title_jp || userData.position || "",
      title_en: userData.title_en || userData.type || "",
      affiliation_jp: userData.affiliation_jp || userData.enterprise || "",
      affiliation_en: userData.affiliation_en || "",
    });
    setLanguage(lang);
    setModalOpen(true);
  };

  // ===== アバター編集ロジック =====

  const handleAvatarClick = () => {
    if (!editable) return;

    const existingUrl = userData.avatar_original || userData.avatar_optimized;
    if (existingUrl) {
      setOriginalImageSrc(existingUrl);
      originalFileRef.current = null;
      setShowCropModal(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleReselect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      originalFileRef.current = file;
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setOriginalImageSrc(reader.result.toString());
          if (!showCropModal) setShowCropModal(true);
        }
      };
      reader.readAsDataURL(file);

      e.target.value = "";
    },
    [showCropModal],
  );

  // S3 に1ファイルをアップロードし file_key を返す
  const uploadSingleToS3 = useCallback(
    async (file: File): Promise<string> => {
      const result = await initiateUpload({
        file_name: file.name,
        file_size: file.size,
        content_type: file.type || "image/jpeg",
        purpose: "avatar" as const,
      }).unwrap();

      const data = result.data as SingleUploadData;
      await uploadFileToS3(file, data.presigned_post);
      return data.file_key;
    },
    [initiateUpload],
  );

  const handleCropConfirm = useCallback(
    async (result: CropResult) => {
      setShowCropModal(false);
      setAvatarPreview(result.base64);
      setIsUploadingAvatar(true);

      const origFile = originalFileRef.current;

      try {
        if (origFile) {
          // 新規画像: オリジナル + クロップ済みの両方をS3にアップロード
          const [originalKey, croppedKey] = await Promise.all([
            uploadSingleToS3(origFile),
            uploadSingleToS3(result.file),
          ]);

          await updateCurrentUser({
            avatar_original: originalKey,
            avatar_optimized: croppedKey,
            avatar_crop_data: result.cropData,
          }).unwrap();
        } else {
          // 既存画像の再クロップ: クロップ済みのみアップロード
          const croppedKey = await uploadSingleToS3(result.file);

          await updateCurrentUser({
            avatar_optimized: croppedKey,
            avatar_crop_data: result.cropData,
          }).unwrap();
        }

        dispatch(userApi.util.invalidateTags(["CurrentUser"]));
      } catch (err) {
        console.error("Avatar upload failed:", err);
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [uploadSingleToS3, updateCurrentUser, dispatch],
  );

  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setOriginalImageSrc(null);
    originalFileRef.current = null;
  }, []);

  // AI 生成画像を選択 → そのままアバターとして保存（生成画像は正方形のためクロップ不要）
  const handleAiGenerated = useCallback(
    async (candidate: GeneratedImageCandidate) => {
      await updateCurrentUser({
        avatar_original: candidate.file_key,
        avatar_optimized: candidate.file_key,
        // 前の写真の切り抜き座標が、再編集時に生成画像へ当たらないよう消す
        avatar_crop_data: { x: 0, y: 0, width: 0, height: 0 },
      }).unwrap();
      setAvatarPreview(candidate.url);
      dispatch(userApi.util.invalidateTags(["CurrentUser"]));
      setShowAiModal(false);
    },
    [updateCurrentUser, dispatch],
  );

  React.useEffect(() => {
    onAvatarEditingChange?.(showCropModal || isUploadingAvatar);
  }, [showCropModal, isUploadingAvatar, onAvatarEditingChange]);

  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) return <SvgLoadingAnimation />;
  if (error)
    return (
      <div>Error: {(error as any).message || "An unknown error occurred"}</div>
    );
  if (!userData) return <div>No user data available</div>;

  // 期待ユーザー（ID / email）が指定されているが `/api/me` の結果と一致しない場合、
  // 別ユーザーのプロフィールを誤って表示・編集させないためにエラーパネルを出す。
  // （招待経由の新規登録直後に、旧セッションで解決された /api/me 結果が残る事故への保険）
  if (isExpectedUserMismatch(userData, expectedUserId, expectedEmail)) {
    return (
      <MismatchErrorPanel>
        セッション情報が取得できませんでした。お手数ですが、ブラウザを一度閉じてから招待リンクを開き直してください。
      </MismatchErrorPanel>
    );
  }

  const displayName =
    userData.display_name_jp ||
    userData.display_name_en ||
    userData.stage_name ||
    userData.stage_name_en ||
    "未登録";
  // card バリアントのカード左辺に縦書きで装飾する英語表示名（オファー作成カードと同じ）。
  const displayNameEn =
    userData.display_name_en || userData.stage_name_en || "";
  const titleText =
    userData.title_jp ||
    userData.title_en ||
    userData.position ||
    userData.type ||
    "";
  const affiliationText =
    userData.affiliation_jp ||
    userData.affiliation_en ||
    userData.enterprise ||
    "";
  const avatarSrc =
    avatarPreview || userData.avatar_optimized || userData.avatar_original;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {variant === "card" ? (
        // オファー作成時のユーザーカードと同じデザインのプレビュー。
        // アバター → アバター編集、テキスト → プロフィール編集モーダル。
        <CardWrapper>
          <OfferUserCardFrame as="div" $selected={false} $variant="arc">
            <AvatarOverlayAnchor>
              <Avatar
                size={ARC_AVATAR_SIZE_PX}
                src={avatarSrc}
                editable={editable}
                onClick={editable ? handleAvatarClick : undefined}
                name={displayName}
              />
              {editable && !avatarSrc && (
                <AiGenerateOverlay>
                  <AiGenerateButton onClick={() => setShowAiModal(true)} />
                </AiGenerateOverlay>
              )}
            </AvatarOverlayAnchor>
            <CardTextTrigger
              $editable={editable}
              onClick={editable ? () => openProfileModal("JP") : undefined}
            >
              <CardLabel className="heading-spacing-18">
                {displayName}
              </CardLabel>
              {titleText && (
                <CardLabel className="bodytext-13 u-line-height-200">
                  {titleText}
                </CardLabel>
              )}
              {affiliationText && (
                <CardLabel className="bodytext-11 u-text-light-gray u-line-height-100">
                  {affiliationText}
                </CardLabel>
              )}
            </CardTextTrigger>
            {qrUrl && (
              <ToolTip description="マイサイトを編集">
                <QrTrigger
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQrClick?.();
                  }}
                  aria-label="マイサイトを編集"
                >
                  <QRCodeSVG value={qrUrl} size={56} />
                </QrTrigger>
              </ToolTip>
            )}
          </OfferUserCardFrame>
          {displayNameEn && (
            <ToolTip description={editable ? "英語プロフィールを編集" : ""}>
              <RotatedDisplayNameEnTrigger
                $editable={editable}
                aria-hidden={editable ? undefined : true}
                role={editable ? "button" : undefined}
                onClick={editable ? () => openProfileModal("EN") : undefined}
              >
                {displayNameEn}
              </RotatedDisplayNameEnTrigger>
            </ToolTip>
          )}
        </CardWrapper>
      ) : (
        <div className="u-col-center" style={{ padding: "16px 0px 16px" }}>
          <AvatarOverlayAnchor>
            <Avatar
              size={size}
              src={avatarSrc}
              editable={editable}
              onClick={handleAvatarClick}
              name={
                userData.display_name_jp ||
                userData.display_name_en ||
                userData.stage_name
              }
            />
            {editable && !avatarSrc && (
              <AiGenerateOverlay>
                <AiGenerateButton onClick={() => setShowAiModal(true)} />
              </AiGenerateOverlay>
            )}
          </AvatarOverlayAnchor>
          {isUploadingAvatar && (
            <div
              className="bodytext-11 u-text-light-gray"
              style={{ marginTop: 4 }}
            >
              アップロード中...
            </div>
          )}
          <ProfileSection
            onClick={editable ? () => openProfileModal("JP") : undefined}
          >
            <ul className="u-col u-gap4">
              <li className="heading-spacing-18 u-pt4">{displayName}</li>
              <li className="bodytext-13 u-line-height-200">{titleText}</li>
              <li className="u-line-height-100 bodytext-11 u-text-light-gray">
                {affiliationText}
              </li>
            </ul>
          </ProfileSection>
          {!hideBadges && (
            <div
              className="u-row-center u-pt8 u-wrap"
              style={{ gap: "16px 16px" }}
            >
              <IdentityBadge />
              <NDASignedBadge status="NDA締結済み" />
            </div>
          )}
        </div>
      )}

      {/* AI 画像生成モーダル */}
      {showAiModal && (
        <AiImageGeneratorModal
          scene="image_avatar"
          hasCurrentAvatar={Boolean(
            userData.avatar_optimized || userData.avatar_original,
          )}
          onSelect={handleAiGenerated}
          onClose={() => setShowAiModal(false)}
        />
      )}

      {/* ImageEditorModal（クロップ）。AI 生成への導線はこのモーダル内に置く */}
      {showCropModal && originalImageSrc && (
        <ImageEditorModal
          imageSrc={originalImageSrc}
          aspectRule={ASPECT_RULES.avatar}
          cropShape="round"
          outputFormat="image/jpeg"
          outputFileName="avatar-cropped.jpg"
          initialCropData={userData.avatar_crop_data ?? undefined}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          onReselect={handleReselect}
          renderExtra={() => (
            <div className="u-row-center u-w100" style={{ marginTop: 8 }}>
              <AiGenerateButton
                onClick={() => {
                  setShowCropModal(false);
                  setShowAiModal(true);
                }}
              />
            </div>
          )}
        />
      )}

      {/* プロフィール編集モーダル */}
      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          title="プロフィールを編集"
          size="default"
        >
          {isWideProfileForm ? (
            // 広い画面: JP / EN を切り替えずに左右 2 カラムで全フィールドを表示
            <div className="u-col u-gap24 u-w100">
              {PROFILE_FIELDS.map((field) => (
                <div key={field.key} className="u-row u-gap24 u-w100">
                  <div className="u-fill" style={{ minWidth: 0 }}>
                    {renderProfileInput(field, "JP")}
                  </div>
                  <div className="u-fill" style={{ minWidth: 0 }}>
                    {renderProfileInput(field, "EN")}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // 狭い画面: 従来どおり LanguageSwitcher で JP / EN を切り替え
            <div className="u-col u-gap24 u-w100">
              {PROFILE_FIELDS.map((field) => (
                <React.Fragment key={field.key}>
                  {renderProfileInput(field, language)}
                </React.Fragment>
              ))}
            </div>
          )}
          <ModalFooter>
            {isWideProfileForm ? (
              // space-between レイアウトで StatusIndicator を右端に保つスペーサー
              <span aria-hidden="true" />
            ) : (
              <LanguageSwitcher
                language={language}
                onLanguageChange={setLanguage}
              />
            )}
            <StatusIndicator status={updateStatus} inline />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default UserItem;
